import Section from 'app/frontend/base/components/Section'
import { Button } from '@/components/ui/button'
import { CalendarPlus } from 'lucide-react'
import { useState } from 'react'
import SubscribeCalendarDialog from './components/SubscribeCalendarDialog'
import {
  usePageInfo,
  useUserListEvents,
} from 'app/frontend/base/config/queries'
import usePersistedHashValue from 'app/frontend/base/hooks/usePersistedHashValue'
import EventListTable from 'app/frontend/base/pages/TeamDashboard/subpages/TeamEventOverview/components/EventListTable'
import { eventParticipantStatuses } from 'app/frontend/types/custom/EventParticipantStatus'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  TeamListEventsResponse,
  updateEventParticipantSignup,
  updateEventParticipantStatus,
} from 'app/frontend/base/api/event'
import { displayErrorToast } from 'app/frontend/base/helpers'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { patchTeamEventReferenceCaches } from 'app/frontend/base/pages/TeamDashboard/subpages/TeamEvent/utils/teamEventReferenceCache'
import useBulkUserEventStatusMutation from 'app/frontend/base/pages/TeamDashboard/subpages/TeamEventOverview/components/useBulkUserEventStatusMutation'

const selectableStatuses: wowaudit.EventParticipantReference['status'][] = [
  ...eventParticipantStatuses,
]

const isEventPageValue = (value: string): value is string => /^\d+$/.test(value)
const isEventListTab = (value: string): value is 'upcoming' | 'past' =>
  value === 'upcoming' || value === 'past'
const patchUserEventListCaches = (
  queryClient: ReturnType<typeof useQueryClient>,
  updatedEvent: Pick<wowaudit.EventReference, 'id'> &
    Partial<wowaudit.EventReference>,
) => {
  queryClient.setQueriesData<TeamListEventsResponse>(
    {
      queryKey: ['userEvents', { type: 'list' }],
    },
    (existingData) => {
      if (!existingData) return existingData
      return {
        ...existingData,
        events: existingData.events.map((eventReference) =>
          eventReference.id === updatedEvent.id
            ? { ...eventReference, ...updatedEvent }
            : eventReference,
        ),
      }
    },
  )
}

const UserEvents = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const {
    data: { user },
  } = usePageInfo()
  const { value: tab, setValue: setTab } = usePersistedHashValue<
    'upcoming' | 'past'
  >({
    defaultValue: 'upcoming',
    isValid: isEventListTab,
    hashKey: 'tab',
    persistInLocalStorage: false,
  })
  const { value: pageValue, setValue: setPageValue } =
    usePersistedHashValue<string>({
      defaultValue: '1',
      isValid: isEventPageValue,
      hashKey: 'page',
      persistInLocalStorage: false,
    })
  const page = Math.max(Number(pageValue), 1)
  const { data, isPending, isFetching } = useUserListEvents(page, tab)
  const onTabChange = (nextTab: 'upcoming' | 'past') => {
    setTab(nextTab)
    setPageValue('1')
  }
  const isLoadingEvents =
    isPending || (isFetching && (data?.page ?? page) !== page)
  const [updatingEventId, setUpdatingEventId] = useState<number>()
  const [subscribeOpen, setSubscribeOpen] = useState(false)
  const { bulkUpdateStatus, isBulkUpdating } = useBulkUserEventStatusMutation()

  const { mutate: saveWithComment } = useMutation({
    mutationFn: ({
      teamId,
      participantId,
      status,
      comment,
    }: {
      teamId: number
      participantId: number
      status: wowaudit.EventParticipantReference['status']
      comment: string
    }) =>
      updateEventParticipantSignup(teamId, participantId, { status, comment }),
    onSuccess: (updatedEvent, variables) => {
      patchTeamEventReferenceCaches(queryClient, variables.teamId, updatedEvent)
      patchUserEventListCaches(queryClient, updatedEvent)
      toast.success(t('shared.changes-saved'))
    },
    onError: (error) => displayErrorToast(error, t),
  })

  const { mutate: updateStatus, isPending: isUpdatingStatus } = useMutation({
    mutationFn: ({
      teamId,
      eventParticipantId,
      nextStatus,
    }: {
      teamId: number
      eventParticipantId: number
      nextStatus: wowaudit.EventParticipantReference['status']
    }) => {
      return updateEventParticipantStatus(
        teamId,
        eventParticipantId,
        nextStatus,
      )
    },
    onSuccess: (updatedEvent, variables) => {
      patchTeamEventReferenceCaches(queryClient, variables.teamId, updatedEvent)
      patchUserEventListCaches(queryClient, updatedEvent)
      setUpdatingEventId(undefined)
      toast.success(t('dashboard.team.events.status-updated'))
    },
    onError: (error) => {
      setUpdatingEventId(undefined)
      displayErrorToast(error, t)
    },
  })

  return (
    <Section title='Profile'>
      <h2 className='flex justify-between items-center'>
        <span>{t('profile.header.events')}</span>
        {user ? (
          <Button
            variant='outline'
            className='flex gap-2'
            onClick={() => setSubscribeOpen(true)}
          >
            <CalendarPlus size={16} />
            {t('profile.events.subscribe')}
          </Button>
        ) : null}
      </h2>
      <SubscribeCalendarDialog
        open={subscribeOpen}
        onOpenChange={setSubscribeOpen}
      />
      <EventListTable
        userId={user?.id}
        events={data?.events ?? []}
        page={data?.page ?? page}
        totalPages={data?.totalPages ?? 1}
        isLoadingEvents={isLoadingEvents}
        isUpdatingStatus={isUpdatingStatus}
        updatingEventId={updatingEventId}
        onPageChange={(nextPage) => setPageValue(nextPage.toString())}
        onRowClick={(event) => navigate(event.path)}
        membershipForEvent={() => undefined}
        selectableStatusesForEvent={() => selectableStatuses}
        allowParticipantSelfEdit={true}
        onSelectStatus={({ event, participant, nextStatus }) => {
          if (!participant) return
          if (event.teamId === undefined) return
          const participantId = participant.id
          if (participantId === undefined) return
          setUpdatingEventId(event.id)
          updateStatus({
            teamId: event.teamId,
            eventParticipantId: participantId,
            nextStatus,
          })
        }}
        onSaveWithComment={({ event, participant, nextStatus, comment }) => {
          if (event.teamId === undefined) return
          saveWithComment({
            teamId: event.teamId,
            participantId: participant.id,
            status: nextStatus,
            comment,
          })
        }}
        bulkSelectableStatuses={selectableStatuses}
        isBulkUpdating={isBulkUpdating}
        onBulkSelectStatus={(eventIds, nextStatus, comment) => {
          bulkUpdateStatus({ eventIds, status: nextStatus, comment })
        }}
        tab={tab}
        onTabChange={onTabChange}
      />
    </Section>
  )
}

export default UserEvents
